/**
 * Everything the editor reads off the working document.
 *
 * The one non-trivial getter is `isDirty`, for a specific reason (B7): the `json` column normalises
 * object key order on write — MySQL behaviour, not our code — so a serialised-string comparison
 * reports a dirty document nobody touched. The comparison is therefore BY VALUE: `isSameValue`
 * walks both documents and compares key sets and values, never a serialisation.
 */
import type {
  Activity,
  AssessmentTemplatePayload,
  Screen,
} from '../interfaces'
import type { ReviewRubric, RubricCatalog } from '../types'
import type { AssessmentEditorStore } from './interfaces'

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Deep structural equality, KEY-ORDER-INSENSITIVE — the whole point (B7). Arrays compare
 * positionally (their order is preserved by the round trip and is meaningful); objects compare by
 * key set and value, in whatever order the keys arrive.
 *
 * Unknown keys participate: a document that differs only in a field no schema describes is still a
 * changed document, and one the save would overwrite (B9).
 */
const isSameValue = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false
    }
    return left.every((item, index) => isSameValue(item, right[index]))
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left)
    if (leftKeys.length !== Object.keys(right).length) {
      return false
    }
    return leftKeys.every(key =>
      Object.prototype.hasOwnProperty.call(right, key) && isSameValue(left[key], right[key]),
    )
  }
  return false
}

const getScreens = (state: AssessmentEditorStore): Screen[] =>
  state.workingDocument?.definition.screens ?? []

const getSelectedScreen = (state: AssessmentEditorStore): Screen | null =>
  getScreens(state).find(screen => screen.id === state.selectedScreenId) ?? null

const getCatalog = (state: AssessmentEditorStore): RubricCatalog =>
  state.workingDocument?.rubrics ?? {}

/**
 * The `store`/`update` body, assembled from state alone — no HTTP here. `null` while any required
 * field is missing (§4: all five are required on both verbs). `data` is the working document by
 * reference, deliberately.
 *
 * Exported as a plain function, not only as a getter, because the ACTION that reconciles the media
 * manifest has to return the very same body (`buildSavePayload`) — one assembly, two entry points.
 */
export const toSavePayload = (state: AssessmentEditorStore): AssessmentTemplatePayload | null => {
  if (!state.workingDocument || !state.type || !state.disciplineCode || !state.grade) {
    return null
  }
  return {
    title: state.title,
    type: state.type,
    discipline_code: state.disciplineCode,
    grade: state.grade,
    data: state.workingDocument,
  }
}

export const getters = {
  isLoading: (state: AssessmentEditorStore): AssessmentEditorStore['loading'] => state.loading,
  isSaving: (state: AssessmentEditorStore): AssessmentEditorStore['saving'] => state.saving,
  hasDocument: (state: AssessmentEditorStore): boolean => state.workingDocument !== null,
  getTemplateId: (state: AssessmentEditorStore): AssessmentEditorStore['templateId'] => state.templateId,
  getTitle: (state: AssessmentEditorStore): AssessmentEditorStore['title'] => state.title,
  getType: (state: AssessmentEditorStore): AssessmentEditorStore['type'] => state.type,
  getDisciplineCode: (state: AssessmentEditorStore): AssessmentEditorStore['disciplineCode'] => state.disciplineCode,
  getGrade: (state: AssessmentEditorStore): AssessmentEditorStore['grade'] => state.grade,
  getStatus: (state: AssessmentEditorStore): AssessmentEditorStore['status'] => state.status,

  /** The intro screen's two strings — the kid-facing name and its hook. Distinct from the row's
   *  `getTitle`, which is the catalogue handle a tutor picks from. */
  getDefinitionTitle: (state: AssessmentEditorStore): string =>
    state.workingDocument?.definition.title ?? '',
  getDefinitionDescription: (state: AssessmentEditorStore): string =>
    state.workingDocument?.definition.description ?? '',

  /** The RAW working document — hand this to `useValidateDefinition` and to the save call. Never a
   *  parsed copy (B9): unknown keys must survive the round trip. */
  getWorkingDocument: (state: AssessmentEditorStore): AssessmentEditorStore['workingDocument'] =>
    state.workingDocument,

  /**
   * The `store`/`update` body — see `toSavePayload` above.
   *
   * A READ-ONLY VIEW: the save path deliberately uses the `buildSavePayload` ACTION instead, which
   * reconciles the media manifest and then returns exactly this. A getter cannot mutate, and a
   * manifest that only some save paths refresh is the drift B13.11 exists to make impossible.
   */
  getPayload: (state: AssessmentEditorStore): AssessmentTemplatePayload | null =>
    toSavePayload(state),

  /** Unsaved changes — by value, never by serialised string (B7). */
  isDirty: (state: AssessmentEditorStore): boolean => {
    if (!state.workingDocument || !state.pristine) {
      return false
    }
    return state.title !== state.pristine.title
      || state.type !== state.pristine.type
      || state.disciplineCode !== state.pristine.disciplineCode
      || state.grade !== state.pristine.grade
      || !isSameValue(state.workingDocument, state.pristine.document)
  },

  // ---- screens & selection ----
  getScreens: (state: AssessmentEditorStore): Screen[] => getScreens(state),
  hasScreens: (state: AssessmentEditorStore): boolean => getScreens(state).length > 0,
  getSelectedScreenId: (state: AssessmentEditorStore): AssessmentEditorStore['selectedScreenId'] =>
    state.selectedScreenId,
  getSelectedScreen: (state: AssessmentEditorStore): Screen | null => getSelectedScreen(state),
  /** `-1` when nothing is selected — for "move up / move down" button disabling. */
  getSelectedScreenIndex: (state: AssessmentEditorStore): number =>
    getScreens(state).findIndex(screen => screen.id === state.selectedScreenId),
  getSelectedActivityId: (state: AssessmentEditorStore): AssessmentEditorStore['selectedActivityId'] =>
    state.selectedActivityId,
  getSelectedActivity: (state: AssessmentEditorStore): Activity | null =>
    getSelectedScreen(state)?.activities.find(activity => activity.id === state.selectedActivityId) ?? null,

  // ---- rubric catalog ----
  /** The catalog map itself (`data.rubrics`) — `Record<rubricId, ReviewRubric>`, never
   *  `Activity.rubrics`, which is an array of ids into this (B8). */
  getRubricCatalog: (state: AssessmentEditorStore): RubricCatalog => getCatalog(state),
  /** The catalog as a list, for rendering. Object key order is an accident of insertion and does not
   *  survive the JSON round trip reliably — a display order, not an authored one, which is why there
   *  is no `moveRubricUp/Down`. */
  getRubrics: (state: AssessmentEditorStore): ReviewRubric[] => Object.values(getCatalog(state)),
  getRubricIds: (state: AssessmentEditorStore): string[] => Object.keys(getCatalog(state)),
  hasRubrics: (state: AssessmentEditorStore): boolean => Object.keys(getCatalog(state)).length > 0,
}
